/**
 * Site assignments and plants — which part of the fleet a person reaches.
 *
 * ── THE ONE THING A CONSUMER MUST NOT GET WRONG ──────────────────────────────
 * A WHOLE-SITE ASSIGNMENT IS ONE ROW with no plant and no device, and the editor must send it
 * that way. It must never expand "everything ticked" into the devices that happen to exist —
 * this fleet auto-registers, so a device integrated next week would silently fall outside the
 * assignment, nobody would be watching it, and every screen would look normal. Unticking sends a
 * `deny`, which reads as *except this* and keeps following the site as it grows.
 *
 * ── AND THE ONE A READER MUST NOT ────────────────────────────────────────────
 * `scope: null` on `/me` is UNSCOPED — the whole organisation. An empty `siteIds` is a scoped
 * person with nothing assigned. They are different answers and must render differently: the
 * first is an organisation with no sites, the second is an administrator who has not finished.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { topologyQueryKey } from './topology';

export interface Plant {
  id: string;
  siteId: string;
  name: string;
  derivedFromNodeId: string | null;
  /** How many devices are filed here — what an admin needs before deleting one. */
  deviceCount: number;
  /*
   * THE DECLARED NAMEPLATE (migration 0064). `null` is "nobody has recorded it" and is NOT zero —
   * a plant under construction rates zero and that is a statement, while an unrecorded capacity
   * is an absence. Render the first as a number and the second as an invitation, never as `0 kW`
   * beside real equipment.
   *
   * `dcCapacityKwp` is the specific-yield denominator, and it is the reason these fields exist:
   * raw kWh cannot compare Jamshoro's 459 kW cluster against its 185 kW one, and kWh/kWp can.
   * Anything dividing by it must refuse when it is null rather than substitute a guess.
   */
  ratedKw: number | null;
  dcCapacityKwp: number | null;
  /** `YYYY-MM-DD`. A calendar day, with no time and no zone — see the API's `calendarDate`. */
  installedOn: string | null;
  commissionedOn: string | null;
}

/**
 * What an edit may change. Absent leaves a field alone; explicit `null` clears it.
 *
 * Both halves are load-bearing and the server reads them the same way. `SiteMetadataPage` records
 * what the other spelling cost — a cleared field silently kept its stored value, because the
 * payload could not tell "unchanged" from "emptied".
 */
export interface PlantPatch {
  name?: string;
  ratedKw?: number | null;
  dcCapacityKwp?: number | null;
  installedOn?: string | null;
  commissionedOn?: string | null;
}

export interface Grant {
  id: string;
  siteId: string;
  /** Narrower than the site. Mutually exclusive with `deviceId` — one axis per row. */
  plantId: string | null;
  deviceId: number | null;
  effect: 'allow' | 'deny';
  grantedAt: string;
}

/** What the editor sends. `id` and `grantedAt` are the server's to decide. */
export interface GrantInput {
  siteId: string;
  plantId?: string | null;
  deviceId?: number | null;
  effect?: 'allow' | 'deny';
}

/**
 * The place tier — a region, sub-region or city instead of a list of sites (Lumen-BE 0110).
 *
 * `sub_region` carries the server's spelling, not the screen's. It is the attribute key (0074)
 * and the enum value, and translating it here would mean two vocabularies for one tier; the
 * label belongs in the renderer, which is where `PLACE_LEVELS` puts it.
 */
export type PlaceKind = 'region' | 'sub_region' | 'city';

export interface PlaceGrant {
  id: string;
  kind: PlaceKind;
  /** The place as the fleet spells it. Matched case-insensitively by the server. */
  value: string;
  grantedAt: string;
}

/** What the editor sends for a place. */
export interface PlaceInput {
  kind: PlaceKind;
  value: string;
}

/**
 * A person's whole reach, both tiers, as `GET /users/:id/access` answers.
 *
 * READ BOTH OR NEITHER. A person with no `data` rows and one `places` row reaches an entire
 * region; a reader that looked only at `data` would call that "nothing assigned" and show an
 * administrator a warning about a person who is correctly configured.
 */
export interface UserAccess {
  data: Grant[];
  places: PlaceGrant[];
}

export const scopeKeys = {
  plants: (siteId: string) => ['plants', siteId] as const,
  access: (userId: string) => ['user-access', userId] as const,
};

export function usePlants(siteId: string | null) {
  return useQuery({
    queryKey: scopeKeys.plants(siteId ?? ''),
    enabled: siteId !== null,
    queryFn: () => api.get<{ data: Plant[] }>(`/sites/${siteId}/plants`),
    staleTime: 60_000,
  });
}

export function useCreatePlant(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; derivedFromNodeId?: string }) =>
      api.post<{ plant: Plant }>(`/sites/${siteId}/plants`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: scopeKeys.plants(siteId) }),
  });
}

export function useRenamePlant(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.patch<{ plant: Plant }>(`/plants/${id}`, { name }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: scopeKeys.plants(siteId) }),
  });
}

/**
 * Amend a plant's declared nameplate — capacity and dates, and its name if that is what changed.
 *
 * SEPARATE FROM `useRenamePlant` ON PURPOSE. The two send to the same endpoint and could be one
 * hook, but they are different acts with different blast radii: a rename changes a label on every
 * screen that lists plants, while a capacity edit changes a DENOMINATOR — the specific yield of
 * this plant, and the site's declared-versus-drawn comparison. Keeping them apart means a caller
 * has to have meant the second one.
 *
 * It invalidates the site's topology as well, because `SitePlantPage` renders the plant's declared
 * capacity beside the sum of what the single line draws, and leaving that comparison on a stale
 * half is how a screen tells somebody their two numbers disagree when they have just made them
 * agree.
 */
export function useUpdatePlant(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: PlantPatch }) =>
      api.patch<{ plant: Plant }>(`/plants/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: scopeKeys.plants(siteId) });
      void qc.invalidateQueries({ queryKey: topologyQueryKey(siteId) });
    },
  });
}

/**
 * Delete a plant. Its devices become site-wide, and every assignment naming it disappears.
 *
 * The response says how many devices were released, which is the number the confirmation should
 * have shown — a plant with eight inverters in it is not the same decision as an empty one.
 */
export function useDeletePlant(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ devicesReleased: number }>(`/plants/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: scopeKeys.plants(siteId) });
      void qc.invalidateQueries({ queryKey: ['devices'] });
    },
  });
}

/** Which plant a device belongs to. `null` puts it back to site-wide equipment. */
export function useSetDevicePlant(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, plantId }: { deviceId: number; plantId: string | null }) =>
      api.put<{ deviceId: number; plantId: string | null }>(`/devices/${deviceId}/plant`, {
        plantId,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: scopeKeys.plants(siteId) });
      void qc.invalidateQueries({ queryKey: ['devices'] });
    },
  });
}

export function useUserAccess(userId: string | null) {
  return useQuery({
    queryKey: scopeKeys.access(userId ?? ''),
    enabled: userId !== null,
    queryFn: () => api.get<UserAccess>(`/users/${userId}/access`),
    staleTime: 30_000,
  });
}

/**
 * Replace a person's assignments wholesale.
 *
 * PUT, not incremental add and remove: an assignment is read as a whole by whoever approves it,
 * and a partial failure halfway through would leave somebody with a reach nobody chose — neither
 * what they had nor what was intended.
 */
export function useSetUserAccess(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    /*
     * The scope flag travels WITH the grants, because they are one act: "here is this person's
     * reach". Two calls would leave a window where somebody is marked scoped with nothing
     * assigned — a blank console rather than a restriction. Omitting `scopeKind` leaves it alone,
     * so an edit that only changes the site list says nothing about the flag.
     */
    /*
     * `places` IS ALWAYS SENT, and never left out to mean "unchanged".
     *
     * The server replaces both tiers on every call — an omitted `places` is an empty one, which
     * is what makes this a replacement rather than a patch. Sending the site tier alone would
     * leave somebody reaching a whole region that nothing on the screen had chosen, and the
     * editor would show a reach the server does not hold. `scopeKind` is the one field that is
     * genuinely optional, because it is a flag about the PERSON rather than part of the reach.
     */
    mutationFn: ({
      grants,
      places = [],
      scopeKind,
    }: {
      grants: GrantInput[];
      places?: PlaceInput[];
      scopeKind?: 'organisation' | 'site';
    }) =>
      api.put<UserAccess>(`/users/${userId}/access`, {
        grants,
        places,
        ...(scopeKind === undefined ? {} : { scopeKind }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: scopeKeys.access(userId) });
      void qc.invalidateQueries({ queryKey: ['org-users'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}
